import { logEvent, type Analytics } from 'firebase/analytics';

export const AUTH_STAGE_TELEMETRY_EVENT = 'scribeshadow:auth-stage';

export type AuthStage =
  | 'initialization'
  | 'password_sign_in'
  | 'google_sign_in'
  | 'password_registration'
  | 'token_retrieval'
  | 'profile_hydration'
  | 'session_sign_out';

export type AuthStageOutcome = 'success' | 'error' | 'timeout';

export type AuthStageTelemetry = {
  stage: AuthStage;
  outcome: AuthStageOutcome;
  durationMs: number;
  browserFamily: 'safari' | 'chrome' | 'firefox' | 'edge' | 'other';
  errorCode?: string;
};

type RunAuthStageOptions = {
  timeoutMs: number;
  successSampleRate?: number;
};

let configuredAnalytics: Analytics | null = null;

export const configureAuthTelemetry = (analytics: Analytics | null): void => {
  configuredAnalytics = analytics;
};

const nowMs = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

const browserFamily = (): AuthStageTelemetry['browserFamily'] => {
  if (typeof navigator === 'undefined') return 'other';
  const userAgent = navigator.userAgent || '';
  if (/Edg\//.test(userAgent)) return 'edge';
  if (/Chrome|CriOS/.test(userAgent)) return 'chrome';
  if (/Firefox|FxiOS/.test(userAgent)) return 'firefox';
  if (/Safari\//.test(userAgent)) return 'safari';
  return 'other';
};

const safeErrorCode = (error: unknown): string | undefined => {
  const candidate =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code?: unknown }).code || '')
      : error instanceof Error
        ? error.name
        : '';
  const normalized = candidate.trim().slice(0, 80);
  return /^[A-Za-z0-9_./:-]+$/.test(normalized) ? normalized : undefined;
};

export class AuthStageTimeoutError extends Error {
  readonly code = 'auth/operation-timeout';

  constructor(
    readonly stage: AuthStage,
    readonly timeoutMs: number,
  ) {
    super(`Authentication stage timed out: ${stage}`);
    this.name = 'AuthStageTimeoutError';
  }
}

export const recordAuthStage = (
  stage: AuthStage,
  outcome: AuthStageOutcome,
  durationMs: number,
  error?: unknown,
): void => {
  const detail: AuthStageTelemetry = {
    stage,
    outcome,
    durationMs: Math.max(0, Math.round(durationMs)),
    browserFamily: browserFamily(),
    ...(error ? { errorCode: safeErrorCode(error) } : {}),
  };

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_STAGE_TELEMETRY_EVENT, { detail }));
  }

  if (configuredAnalytics) {
    try {
      logEvent(configuredAnalytics, 'auth_stage', {
        auth_stage: detail.stage,
        outcome: detail.outcome,
        duration_ms: detail.durationMs,
        browser_family: detail.browserFamily,
        ...(detail.errorCode ? { error_code: detail.errorCode } : {}),
      });
    } catch {
      // Authentication must never depend on analytics availability.
    }
  }
};

export const runAuthStage = async <T>(
  stage: AuthStage,
  operation: () => Promise<T>,
  { timeoutMs, successSampleRate = 1 }: RunAuthStageOptions,
): Promise<T> => {
  const startedAt = nowMs();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const operationPromise = Promise.resolve().then(operation);
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new AuthStageTimeoutError(stage, timeoutMs)), timeoutMs);
  });

  try {
    const result = await Promise.race([operationPromise, timeoutPromise]);
    if (successSampleRate >= 1 || Math.random() < Math.max(0, successSampleRate)) {
      recordAuthStage(stage, 'success', nowMs() - startedAt);
    }
    return result;
  } catch (error) {
    recordAuthStage(
      stage,
      error instanceof AuthStageTimeoutError ? 'timeout' : 'error',
      nowMs() - startedAt,
      error,
    );
    throw error;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
};

export const AUTH_STAGE_TIMEOUT_MS = {
  initialization: 10_000,
  signIn: 15_000,
  registration: 20_000,
  token: 10_000,
  profile: 12_000,
  signOut: 8_000,
} as const;
